import React from "react";
import { Route, Routes } from "react-router-dom";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import Layout from "components/Layout";
import * as Sentry from "@sentry/react";
import { GEEKS_USER } from "constants/profile";
import { getDataFromLocalStorage, isAdmin } from "helpers/generalHelper";
import NotFound from "pages/NotFound/NotFound";
import { FULL_ROUTES_PATH } from "constants/routes";
import { NAVBAR_NAMES } from "constants/navbar";
import { VIEW_TESTING } from "constants/testing";
// Lazy like every other page: static imports here end up in the main bundle,
// which every page (most importantly Home) pays for on first load. VolSurfaceData
// alone pulls Plotly (~3.4 MB) and Highcharts through its graph components.
const VolSurfaceData = lazyWithRetry(() => import("pages/VolSurfaceData"));
const Trader = lazyWithRetry(() => import("pages/Trader"));
const TeamRunReadonly = lazyWithRetry(
  () => import("pages/TeamRunReadonly/TeamRunReadonly")
);
const AuditTrail = lazyWithRetry(() => import("pages/AuditTrail"));
const VolSurface = lazyWithRetry(() => import("pages/VolSurface"));
const TradeConfirm = lazyWithRetry(() => import("pages/TradeConfirm"));
const PayoutAnalysisShareRoute = lazyWithRetry(
  () => import("pages/PayoutAnalysisShare/PayoutAnalysisShareRoute")
);
const MatrixShareRoute = lazyWithRetry(
  () => import("pages/MatrixShare/MatrixShareRoute")
);
const MarketAnalysis = lazyWithRetry(() => import("pages/MarketAnalysis"));
const BusinessAnalytics = lazyWithRetry(() => import("pages/BusinessAnalytics"));
const Testing = lazyWithRetry(() => import("pages/Testing"));
const Onboarding = lazyWithRetry(() => import("pages/Onboarding"));
const AuthRoute = lazyWithRetry(() => import("components/AuthRoute"));
const VerifyOtpPage = lazyWithRetry(
  () => import("pages/VerifyOtp/VerifyOtpPage")
);
// const PricerPage = lazyWithRetry(() =>
//   import("../features/pricer_page/components/PricerGridComponent")
// );
const Home = lazyWithRetry(() => import("pages/Home/Home"));
// const NewPricer = lazyWithRetry(() => import("pages/Pricer/Pricer"));
const LoginPage = lazyWithRetry(() => import("pages/LoginPage/LoginPage"));
const ResetPassword = lazyWithRetry(() => import("pages/ResetPassword"));
const ForgetPassword = lazyWithRetry(() => import("pages/ForgetPassword"));
// const TeamRun = lazyWithRetry(() => import("pages/TeamRun"));
const Profile = lazyWithRetry(() => import("pages/Profile"));
const Chat = lazyWithRetry(() => import("pages/Chat/Chat"));

/**
 *  Used for share paths in component to prevent write or redirect path static in code
 */

/**
 * Use this component for any new section of routes (any config object that has a "routes" property
 */
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
export const RenderRoutes = ({ routes }) => {
  const geeksUser = getDataFromLocalStorage(GEEKS_USER);
  const pages = geeksUser?.authorized_permissions;
  const adminUser = isAdmin();
  const authorizedRoutes = pages
    ? routes.filter((route) => {
        if (route.adminOnly) return adminUser;
        return pages?.includes(route.key);
      })
    : routes.filter((route) => !route.adminOnly || adminUser);

  if (authorizedRoutes && authorizedRoutes.length > 0) {
    authorizedRoutes.at(0).path = "/";
  }

  authorizedRoutes.push({
    path: FULL_ROUTES_PATH.AUDIT_TRAIL.auditDetails,
    key: "audit-trail-details",
    auth: true,
    component: <AuditTrail key={"audit-trail-details"} />,
  });

  // Home is served at "/", but keep its canonical "/home" path registered too.
  // A static "/home" outranks the "/:token" share route, so stale bookmarks and
  // reopened tabs resolve to Home instead of falling into the share route's 404.
  const canViewHome = authorizedRoutes.some(
    (route) => route.key === "View-Home"
  );

  function onClickHandle() {
    window.location.href = "/";
  }

  return (
    <SentryRoutes>
      <Route
        key={FULL_ROUTES_PATH.TRADE_CONFIRM.index}
        path={FULL_ROUTES_PATH.TRADE_CONFIRM.index}
        element={<TradeConfirm />}
      />
      <Route
        key={FULL_ROUTES_PATH.MATRIX_SHARE.index}
        path={FULL_ROUTES_PATH.MATRIX_SHARE.index}
        element={<MatrixShareRoute />}
      />
      <Route
        key={FULL_ROUTES_PATH.PAYOUT_ANALYSIS_SHARE.index}
        path={FULL_ROUTES_PATH.PAYOUT_ANALYSIS_SHARE.index}
        element={<PayoutAnalysisShareRoute />}
      />
      {authorizedRoutes.map((route) => {
        return route.auth ? (
          <Route
            key={route.key}
            path={route.path}
            element={
              <AuthRoute>
                <Layout navTabs={authorizedRoutes}>{route.component}</Layout>
              </AuthRoute>
            }
          />
        ) : (
          <Route key={route.key} path={route.path} element={route.component} />
        );
      })}
      {canViewHome && (
        <Route
          key={FULL_ROUTES_PATH.HOME.index}
          path={FULL_ROUTES_PATH.HOME.index}
          element={
            <AuthRoute>
              <Layout navTabs={authorizedRoutes}>
                <Home />
              </Layout>
            </AuthRoute>
          }
        />
      )}
      <Route
        path="*"
        element={
          geeksUser &&
          window.location.pathname === FULL_ROUTES_PATH.SIGN_IN.index ? (
            <Home />
          ) : (
            <NotFound onClick={onClickHandle} />
          )
        }
      />
    </SentryRoutes>
  );
};

const ROUTES = [
  {
    path: FULL_ROUTES_PATH.HOME.index,
    key: "View-Home",
    auth: true,
    component: <Home />,
    label: NAVBAR_NAMES.HOME,
  },

  {
    path: FULL_ROUTES_PATH.SIGN_IN.index,
    key: "sign_in",
    component: <LoginPage />,
  },
  {
    path: FULL_ROUTES_PATH.VERIFY_OTP.index,
    key: "verify_otp",
    component: <VerifyOtpPage />,
  },
  {
    path: FULL_ROUTES_PATH.RESET_PASSWORD.ResetPassword,
    key: "reset_password",
    component: <ResetPassword />,
  },
  {
    path: FULL_ROUTES_PATH.FORGET_PASSWORD.forgetPassword,
    key: "forget_password",
    component: <ForgetPassword />,
  },
  {
    path: FULL_ROUTES_PATH.ONBOARDING.index,
    key: "onboarding",
    component: <Onboarding />,
  },
  {
    path: FULL_ROUTES_PATH.TRADER.index,
    key: "view-trader",
    auth: true,
    label: NAVBAR_NAMES.TRADER,
    component: <Trader />,
  },
  {
    path: FULL_ROUTES_PATH.TEAM_RUN_READONLY.index,
    key: "View-TeamRunReadOnly",
    auth: true,
    label: NAVBAR_NAMES.TEAM_RUN_READONLY,
    component: <TeamRunReadonly />,
  },
  {
    path: FULL_ROUTES_PATH.AUDIT_TRAIL.index,
    key: "View-AuditTrail",
    auth: true,
    label: NAVBAR_NAMES.AUDIT_TRAIL,
    component: <AuditTrail />,
  },
  {
    path: FULL_ROUTES_PATH.AUDIT_TRAIL.auditDetails,
    key: "view-audit-trail-details",
    auth: true,
    component: <AuditTrail key={"audit-trail-details"} />,
  },

  // {
  //   path: FULL_ROUTES_PATH.PRICER.index,
  //   key: "pricer",
  //   auth: true,
  //   component: <NewPricer />,
  // label: NAVBAR_NAMES.PRICER
  // },
  // {
  //   path: FULL_ROUTES_PATH.TEAM_RUN.index,
  //   key: "team-run",
  //   auth: true,
  // label: NAVBAR_NAMES.TEAM_RUN
  //   component: <TeamRun />,
  // },
  {
    path: FULL_ROUTES_PATH.VOL_SURFACE.index,
    key: "View-VolSurface",
    auth: true,
    label: NAVBAR_NAMES.VOL_SURFACE,
    component: <VolSurface />,
  },

  {
    path: FULL_ROUTES_PATH.MARKET_ANALYSIS.index,
    key: "View-MarketAnalysis",
    auth: true,
    label: NAVBAR_NAMES.MARKET_ANALYSIS,
    component: <MarketAnalysis />,
  },
  {
    path: FULL_ROUTES_PATH.BUSINESS_ANALYTICS.index,
    key: "View-BusinessAnalytics",
    auth: true,
    label: NAVBAR_NAMES.BUSINESS_ANALYTICS,
    component: <BusinessAnalytics />,
  },
  {
    path: FULL_ROUTES_PATH.TESTING.index,
    key: VIEW_TESTING,
    auth: true,
    adminOnly: true,
    label: NAVBAR_NAMES.TESTING,
    component: <Testing />,
  },
  {
    path: FULL_ROUTES_PATH.CHAT.index,
    key: "View-Chat",
    auth: true,
    label: NAVBAR_NAMES.CHAT,
    component: <Chat />,
  },
  {
    path: FULL_ROUTES_PATH.VOL_SURFACE_Data.index,
    key: "View-Data",
    auth: true,
    component: <VolSurfaceData />,
    label: NAVBAR_NAMES.VOL_SURFACE_DATA,
  },
  {
    path: FULL_ROUTES_PATH.PROFILE.index,
    key: "View-Profile",
    auth: true,
    component: <Profile />,
  },
  {
    path: FULL_ROUTES_PATH.TRADE_CONFIRM.index,
    key: "trade-confirm",
    component: <TradeConfirm />,
  },
];

export const ROUTES_PATH = FULL_ROUTES_PATH;

export default ROUTES;
